import { lazy } from 'react';

import type { RouteConfig } from '../../types';

// Lazy load layouts
const DashboardLayout = lazy(() => import('@/layouts/DashboardLayout'));
const ClientsLayout = lazy(() => import('@/pages/clients/layout'));
const ClientLayout = lazy(() => import('@/pages/clients/[clientId]/layout'));
const JobSpecificationLayout = lazy(
  () => import('@/pages/clients/[clientId]/jobSpecifications/[jobSpecificationId]/layout')
);

// Lazy load client pages
const ClientsPage = lazy(() => import('@/pages/clients'));
const CreateClientPage = lazy(() => import('@/pages/clients/create'));
const ClientInfoPage = lazy(() => import('@/pages/clients/[clientId]/info'));
const UpdateClientPage = lazy(() => import('@/pages/clients/[clientId]/update'));
const ClientJobSpecsPage = lazy(() => import('@/pages/clients/[clientId]/jobSpecifications'));
const ClientContractsPage = lazy(() => import('@/pages/clients/[clientId]/contracts'));
const CreateClientContractPage = lazy(() => import('@/pages/clients/[clientId]/contracts/create'));
const ContractLayout = lazy(() => import('@/pages/clients/[clientId]/contracts/[contractId]/layout'));
const ViewClientContractPage = lazy(() => import('@/pages/clients/[clientId]/contracts/[contractId]/view'));
const UpdateClientContractPage = lazy(() => import('@/pages/clients/[clientId]/contracts/[contractId]/update'));
const ContractJobSpecsPage = lazy(() => import('@/pages/clients/[clientId]/contracts/[contractId]/jobSpecifications'));
const ContractTaskManagementPage = lazy(
  () => import('@/pages/clients/[clientId]/contracts/[contractId]/taskManagement')
);
const CreateContractJobSpecPage = lazy(
  () => import('@/pages/clients/[clientId]/contracts/[contractId]/jobSpecifications/create')
);
const ContractJobSpecLayout = lazy(
  () => import('@/pages/clients/[clientId]/contracts/[contractId]/jobSpecifications/[jobSpecificationId]/layout')
);
const ViewContractJobSpecPage = lazy(
  () => import('@/pages/clients/[clientId]/contracts/[contractId]/jobSpecifications/[jobSpecificationId]/view')
);
const UpdateContractJobSpecPage = lazy(
  () => import('@/pages/clients/[clientId]/contracts/[contractId]/jobSpecifications/[jobSpecificationId]/update')
);
const ContractHistoryPage = lazy(() => import('@/pages/clients/[clientId]/contracts/[contractId]/history'));
const CreateClientJobSpecPage = lazy(() => import('@/pages/clients/[clientId]/jobSpecifications/create'));
const ViewClientJobSpecPage = lazy(
  () => import('@/pages/clients/[clientId]/jobSpecifications/[jobSpecificationId]/view')
);
const UpdateClientJobSpecPage = lazy(
  () => import('@/pages/clients/[clientId]/jobSpecifications/[jobSpecificationId]/update')
);

/**
 * Client routes
 * Handles client management and client-specific job specifications
 */
export const clientRoutes: RouteConfig = {
  path: '/clients',
  layout: DashboardLayout,
  children: [
    {
      path: '',
      layout: ClientsLayout,
      children: [
        {
          path: '',
          element: ClientsPage,
        },
        {
          path: '/create',
          element: CreateClientPage,
        },
      ],
    },
    {
      path: '/:clientId',
      layout: ClientLayout,
      children: [
        {
          path: '/info',
          element: ClientInfoPage,
        },
        {
          path: '/update',
          element: UpdateClientPage,
        },
        {
          path: '/jobSpecifications',
          element: ClientJobSpecsPage,
        },
        {
          path: '/contracts',
          element: ClientContractsPage,
        },
        {
          path: '/contracts/create',
          element: CreateClientContractPage,
        },
        {
          path: '/contracts/:contractId',
          layout: ContractLayout,
          children: [
            {
              path: '/view',
              element: ViewClientContractPage,
            },
            {
              path: '/update',
              element: UpdateClientContractPage,
            },
            {
              path: '/jobSpecifications',
              element: ContractJobSpecsPage,
            },
            {
              path: '/jobSpecifications/create',
              element: CreateContractJobSpecPage,
            },
            {
              path: '/taskManagement',
              element: ContractTaskManagementPage,
            },
            {
              path: '/history',
              element: ContractHistoryPage,
            },
            {
              path: '/jobSpecifications/:jobSpecificationId',
              layout: ContractJobSpecLayout,
              children: [
                {
                  path: '/view',
                  element: ViewContractJobSpecPage,
                },
                {
                  path: '/update',
                  element: UpdateContractJobSpecPage,
                },
              ],
            },
          ],
        },
        {
          path: '/jobSpecifications/create',
          element: CreateClientJobSpecPage,
        },
        {
          path: '/jobSpecifications/:jobSpecificationId',
          layout: JobSpecificationLayout,
          children: [
            {
              path: '/view',
              element: ViewClientJobSpecPage,
            },
            {
              path: '/update',
              element: UpdateClientJobSpecPage,
            },
          ],
        },
      ],
    },
  ],
};
